import clsx from 'clsx';
import * as React from 'react';

import { isTextRangeSelection, TextRange } from '@gitbook/slate';
import type { InlineProps } from '@gitbook/slate-react';

import { useIsHiddenOnDiffSide } from '../../Diff';

export type VoidInlineFrameProps = InlineProps & {
    onClick?: React.MouseEventHandler<HTMLSpanElement>;
    onContextMenu?: React.MouseEventHandler<HTMLSpanElement>;
    className?: string;
    /**
     * Indicates if the inline only contains text.
     * @default false
     */
    containsTextOnly?: boolean;
};

/**
 * Wrap an inline with selection controls.
 */
export function VoidInlineFrame({
    ref,
    ...props
}: VoidInlineFrameProps & { ref?: React.Ref<HTMLSpanElement> }): React.ReactElement {
    const isHiddenOnDiffSide = useIsHiddenOnDiffSide(props.node);
    const className = clsx(
        'select-none',
        !props.containsTextOnly && 'inline-flex',
        props.className
    );
    if (props.editor.readOnly) {
        if (isHiddenOnDiffSide) {
            return (
                <span ref={ref} {...props.attributes} hidden>
                    {props.children}
                </span>
            );
        }

        return (
            <span ref={ref} {...props.attributes} className={className} onClick={props.onClick}>
                {props.children}
            </span>
        );
    }

    return (
        <span
            ref={ref}
            {...props.attributes}
            className={className}
            data-selected={props.isFocused || props.isSelected}
            onClick={(event) => {
                props.onClick?.(event);

                if (event.isDefaultPrevented()) {
                    return;
                }

                props.editor.change((change) => {
                    if (!isTextRangeSelection(change.value.selection)) {
                        change.selectText(
                            TextRange.fromBlockRange(change.value.selection, change.value.document)
                        );
                    }
                    change.collapseToStartOf(props.node).focus();
                });
            }}
            onContextMenu={(event) => {
                props.onContextMenu?.(event);
            }}
        >
            {props.children}
        </span>
    );
}
